import { computed, ComputedRef, inject, provide } from "vue";
import { TemplateSize } from "@fresh8/types/src";
import { injectAdTag } from "./brand";

export const MOBILE_LEADERBOARD = {
  name: "mobile-leaderboard",
  width: 0,
  height: 50,
  responsive: true
};

export const MOBILE_LEADERBOARD_FIXED = {
  name: "mobile-leaderboard",
  width: 320,
  height: 50,
  responsive: false
};

export const LMLB = {
  name: "large-mobile-leaderboard",
  width: 320,
  height: 100,
  responsive: false
};

export const SMALL_RESPONSIVE = {
  name: "small-responsive",
  width: 0,
  height: 160,
  responsive: true
};

export const LARGE_RESPONSIVE = {
  name: "large-responsive",
  width: 0,
  height: 270,
  responsive: true
};

export const RESPONSIVE_SIZES: TemplateSize[] = [
  MOBILE_LEADERBOARD,
  SMALL_RESPONSIVE,
  LARGE_RESPONSIVE
];

export const MPU = { name: "mpu", width: 300, height: 250, responsive: false };

export const LARGE_MPU = {
  name: "large-mpu",
  width: 300,
  height: 600,
  responsive: false
};

export const SKYSCRAPER = {
  name: "skyscraper",
  width: 160,
  height: 600,
  responsive: false
};

export const LEADERBOARD = {
  name: "leaderboard",
  width: 728,
  height: 90,
  responsive: false
};

export const LARGE_LEADERBOARD = {
  name: "large-leaderboard",
  width: 970,
  height: 90,
  responsive: false
};

export const BILLBOARD = {
  name: "billboard",
  width: 970,
  height: 250,
  responsive: false
};

export const LONG_MOBILE_LEADERBOARD = {
  name: "mobile-leaderboard",
  width: 980,
  height: 50,
  responsive: false
};

export const FIXED_SIZES: TemplateSize[] = [
  LONG_MOBILE_LEADERBOARD,
  LEADERBOARD,
  LARGE_LEADERBOARD,
  BILLBOARD,
  MPU,
  LARGE_MPU,
  MOBILE_LEADERBOARD_FIXED,
  LMLB,
  SKYSCRAPER
];

type DifPointCalcFn = (
  main: { width: number; height: number },
  point: TemplateSize
) => number;

const difPointCalc: DifPointCalcFn = (main, point) => {
  const difW = main.width - point.width;
  const difH = main.height - point.height;
  return difW * difW + difH * difH;
};

export function findTemplateBySize({
  height = 0,
  width = 0,
  responsive
}: {
  width?: number;
  height?: number;
  responsive?: boolean;
}): TemplateSize {
  if (responsive) {
    // if exact match exists
    const exactMatch = RESPONSIVE_SIZES.find(sizeR => sizeR.height === height);

    if (exactMatch) {
      return exactMatch;
    }

    return RESPONSIVE_SIZES.reduce(
      (prev, curr) =>
        Math.abs(curr.height - height) >= Math.abs(prev.height - height)
          ? prev
          : curr,
      MOBILE_LEADERBOARD
    );
  } else {
    // FIXED_SIZES
    return FIXED_SIZES.reduce((prev, curr) => {
      const currPointDif = difPointCalc({ height, width }, curr);
      const prevPointDif = difPointCalc({ height, width }, prev);
      return currPointDif < prevPointDif ? curr : prev;
    }, MPU);
  }
}

export function useTemplateSize(
  adTag = injectAdTag()
): { size: ComputedRef<TemplateSize> } {
  const size = computed<TemplateSize>(() => {
    const height = adTag?.adTagSize.value?.height || 0;
    const width = adTag?.adTagSize.value?.width || 0;

    return findTemplateBySize({
      height,
      width,
      responsive: adTag?.responsive.value
    });
  });

  return {
    size
  };
}
export type TemplateSizeContext = ReturnType<typeof useTemplateSize>;
export const TemplateSizeSymbol = Symbol("TemplateSizesSymbol");

export function provideTemplateSize(
  templateSizeContext: TemplateSizeContext
): void {
  provide(TemplateSizeSymbol, templateSizeContext);
}
export function injectTemplateSize(): TemplateSizeContext | undefined {
  return inject<TemplateSizeContext>(TemplateSizeSymbol);
}
